import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CardsPoints from "../components/CardsPoints";
import SeeMore from "../components/SeeMore";
import ReflexSelect from "../components/ReflexSelect";
import Cards from "../components/Cards";
import { getMesMaisRecente, obterMesesMaisRecentes, temGatilhoNaoAtingido, isIndicadorGatilhoNaoAtingido, removerDuplicadosPriorizandoPleito } from "../utils";
import Vision from "../components/Vision";
import useReflex from "../hooks/useReflex";
import useUsuario from "~fetchs/useUsuario";
import ModalReflex from "../components/ModalReflex";
import useModalAction from "~components/Modal/hooks/useModalAction";

const LINK_SOLICITAR_PLEITO = "https://vcimentos.capworkflow.com/Pages/NewRequest.aspx?params=V29ya2Zsb3dJZD0yMzQy";

const normalizarTipo = (tipo) => String(tipo || "").trim().toUpperCase();
const isTipoReflex = (tipo) => normalizarTipo(tipo).startsWith("REFLEX");
const isTipoCampanha = (tipo) => normalizarTipo(tipo).startsWith("CAMPANHA");
const KPIS_RESUMO_COORDENACAO = new Set(["COORDENACAO CRC", "VENDEDOR SAFE"]);

const isResumoCoordenacaoItem = (item) => {
  const kpi = String(item?.kpi || "").trim().toUpperCase();
  return KPIS_RESUMO_COORDENACAO.has(kpi);
};

const getResumoCoordenacaoPontuacao = (item) => {
  if (!item?.atingimentos) return 0;

  const candidatos = [
    item.atingimentos.final,
    item.atingimentos.ponderadoProjetadoFinal,
    item.atingimentos.ponderadoSemCredito,
    item.atingimentos.realComCredito,
    item.atingimentos.real,
    item.atingimentos.projetado,
  ];

  for (const valor of candidatos) {
    const numero = Number(valor);
    if (Number.isFinite(numero)) {
      return numero;
    }
  }

  return 0;
};

const getCampanhaPontuacao = (item) => {
  if (!item?.atingimentos) return 0;

  return Number(item.atingimentos.real) || 0;
};

const getPontosSemCredito = ({ isCampanha, atingimentos, useProjected }) => {
  if (!atingimentos) return 0;

  if (isCampanha) {
    return getCampanhaPontuacao({ atingimentos });
  }

  return Number(
    useProjected ? atingimentos.ponderadoProjetadoFinal : atingimentos.ponderadoSemCredito
  ) || 0;
};

const ReflexCurrentMonth = () => {
 const [selectedVendedorId, setSelectedVendedorId] = useState(null);
const [selectedVendedorMes, setSelectedVendedorMes] = useState(null);
const [vendedorSeminfo, setVendedorSeminfo] = useState(false);
const [open, setOpen] = useState(false);
const { reflex, loading } = useReflex()
const vendedores = useMemo(() => reflex, [reflex]);
const pathname = globalThis.location?.pathname || "";
const isMesAtualPath = pathname === "/reflex/mes-atual";
const isHistoricoPath = pathname === "/reflex/historico";
const [mostrarPleito, setMostrarPleito] = useState(false);

const vendedoresParaSelect = useMemo(() => {
  if (!isMesAtualPath || !Array.isArray(vendedores) || vendedores.length === 0) {
    return vendedores;
  }

  const todosMeses = vendedores.flatMap((vendedor) => vendedor?.meses || []);
  const mesGlobal = getMesMaisRecente(todosMeses)?.mes;

  if (!mesGlobal) return vendedores;

  const filtrados = vendedores.filter((vendedor) =>
    (vendedor?.meses || []).some((mes) => mes.mes === mesGlobal)
  );

  return filtrados.length > 0 ? filtrados : vendedores;
}, [isMesAtualPath, vendedores]);

const { isModalOpenAction, closeModalAction, openModalAction } = useModalAction();

const selectOptions = useMemo(() => {
  if (vendedoresParaSelect.length > 1) {
    return [
      { label: 'Visão Geral', value: 0 },
      ...vendedoresParaSelect.map((vendedor) => ({
        label: vendedor.nomeVendedor,
        value: vendedor.id,
      })),
    ];
  }

  return [];
}, [vendedoresParaSelect]);



const { usuario } = useUsuario();


const vendedorSelecionado = useMemo(() => {
  if (!vendedoresParaSelect?.length) return null;

  if (vendedoresParaSelect.length === 1) return vendedoresParaSelect[0];

  if (selectedVendedorId === 0) return vendedoresParaSelect;

  return vendedoresParaSelect.find(v => v.id === selectedVendedorId) || null;
}, [selectedVendedorId, vendedoresParaSelect]);


const getMesAtual = useCallback((meses) => {
  if (selectedVendedorMes && meses) {
    const mesEncontrado = meses.find(m => m.mes === selectedVendedorMes.value);
    if (mesEncontrado) return mesEncontrado;
  }
  return getMesMaisRecente(meses);
}, [selectedVendedorMes]);


const filtrarReflexPorPleito = useCallback((items) => {
  if (!isHistoricoPath || !Array.isArray(items) || items.length === 0) return items;

  const existePleito = items.some((item) => item?.pleito === true);
  if (!existePleito) return items;

  return items.filter((item) =>
    mostrarPleito ? item?.pleito === true : item?.pleito !== true
  );
}, [isHistoricoPath, mostrarPleito]);


const parseMes = (mesStr) => {
  if (!mesStr) return null;
  const mesesMap = {
    jan: 0, fev: 1, mar: 2, abr: 3, mai: 4, jun: 5,
    jul: 6, ago: 7, set: 8, out: 9, nov: 10, dez: 11,
  };
  const [mesAbrev, anoStr] = String(mesStr).toLowerCase().split("/");
  const ano = Number.parseInt(anoStr, 10);
  if (Number.isNaN(ano)) return null;
  const monthIndex = mesesMap[mesAbrev] ?? 0;
  return new Date(2000 + ano, monthIndex, 1);
};

const getMesResumoCoordenacao = useCallback((vendedor) => {
  const mesAtual = getMesAtual(vendedor?.meses);
  if (!mesAtual?.reflex?.length) return { mesAtual, allItems: [], isResumoCoordenacao: false };

  const allItems = filtrarReflexPorPleito(mesAtual.reflex) || [];
  const isResumoCoordenacao =
    allItems.length === 1 && isResumoCoordenacaoItem(allItems[0]);

  return { mesAtual, allItems, isResumoCoordenacao };
}, [filtrarReflexPorPleito, getMesAtual]);


const points = useMemo(() => {
  if (!vendedorSelecionado) return null;

  let vendedorSelecionadoReal = vendedorSelecionado;

  if (Array.isArray(vendedorSelecionado) && vendedorSelecionado.length > 1) {
    if (selectedVendedorId === 0) {
      const encontrado = vendedorSelecionado.find(
        v => Number(v.id) === Number(usuario?.id)
      );

      if (encontrado) {
        vendedorSelecionadoReal = encontrado;
      }
    } else {
      const encontrado = vendedorSelecionado.find(
        v => Number(v.id) === Number(selectedVendedorId)
      );

      if (encontrado) vendedorSelecionadoReal = encontrado;
    }
  }

  if (!vendedorSelecionadoReal?.meses?.length) return null;


  const converterMes = (abreviacao) => {
    const mapa = {
      jan: "Janeiro",
      fev: "Fevereiro",
      mar: "Março",
      abr: "Abril",
      mai: "Maio",
      jun: "Junho",
      jul: "Julho",
      ago: "Agosto",
      set: "Setembro",
      out: "Outubro",
      nov: "Novembro",
      dez: "Dezembro",
    };
    return mapa[abreviacao.toLowerCase()] || abreviacao;
  };

  let mesAtual = null;

  if (selectedVendedorMes) {
    mesAtual = vendedorSelecionadoReal.meses.find(
      m => m.mes === selectedVendedorMes.value
    );
    setVendedorSeminfo(false);
    if (!mesAtual) {
      const mesesDisponiveis = vendedorSelecionadoReal.meses.map(m => {
        const [mes, ano] = m.mes.split("/"); // ex: "jan/25"
        return `${converterMes(mes)} de 20${ano}`;
      });

     setVendedorSeminfo(
       `Não foi possível encontrar nenhum dado para o mês selecionado. Meses disponíveis: ${mesesDisponiveis.join(", ")}`
      );

      return null;
    }

  } else {
    mesAtual = getMesAtual(vendedorSelecionadoReal.meses);
    setVendedorSeminfo(false);
  }

  if (!mesAtual?.reflex?.length) return null;

  const allItems = filtrarReflexPorPleito(mesAtual.reflex);
  if (!allItems?.length) return null;

  const isResumoCoordenacao =
    allItems.length === 1 && isResumoCoordenacaoItem(allItems[0]);

  if (isResumoCoordenacao) {
    const itemResumo = allItems[0];
    const pontuacaoResumo = getResumoCoordenacaoPontuacao(itemResumo);
    const percentualResumo = Number(
      itemResumo.percentualSalarioFinal ??
      itemResumo.percentualSalarioSemCredito ??
      itemResumo.percentualSalarioSemCreditoSemCampanha ??
      0
    );

    return {
      id: vendedorSelecionadoReal.id,
      codigoVendedor: vendedorSelecionadoReal.codigoVendedor,
      nomeVendedor: vendedorSelecionadoReal.nomeVendedor,
      kpiCampanha: null,
      pontuacaoAtual: pontuacaoResumo.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      pontuacaoCampanha: (0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      pontuacaoFinal: pontuacaoResumo.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      percentualSalarioSemCreditoSemCampanha: percentualResumo,
      percentualSalarioFinal: percentualResumo,
    };
  }



  const pontuacaoAtual = allItems
    .filter(item => isTipoReflex(item.tipo) && item.atingimentos?.ponderadoProjetadoFinal != null)
    .reduce((acc, item) => acc + item.atingimentos.ponderadoProjetadoFinal, 0);

  const pontuacaoCampanha = allItems
    .filter(item => isTipoCampanha(item.tipo))
    .reduce((acc, item) => acc + getCampanhaPontuacao(item), 0);

  const campanhaItem = allItems.find(item => isTipoCampanha(item.tipo));
  const kpiCampanha = campanhaItem?.kpi ?? null;

  const reflexItems = allItems.filter(item => isTipoReflex(item.tipo));

  const mediaSemCredito =
    reflexItems.reduce(
      (acc, item) => acc + Number(item.percentualSalarioSemCreditoSemCampanha || 0),
      0
    ) / (reflexItems.length || 1);

  const mediaFinal =
    allItems.reduce(
      (acc, item) => acc + Number(item.percentualSalarioFinal  || 0),
      0
    ) / (allItems.length || 1);

  const gatilhoNaoAtingido = temGatilhoNaoAtingido(allItems);
  const shouldZeroByGatilho = isHistoricoPath && gatilhoNaoAtingido;
  const shouldZeroByPontuacaoMinima = pontuacaoAtual < 100;
  const shouldZero = shouldZeroByGatilho || shouldZeroByPontuacaoMinima;
  const pontuacaoFinal = shouldZero ? 0 : Math.min(pontuacaoAtual + pontuacaoCampanha, 500);

  return {
    id: vendedorSelecionadoReal.id,
    codigoVendedor: vendedorSelecionadoReal.codigoVendedor,
    nomeVendedor: vendedorSelecionadoReal.nomeVendedor,
    kpiCampanha: kpiCampanha,
    pontuacaoAtual: pontuacaoAtual.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    pontuacaoCampanha: pontuacaoCampanha.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    pontuacaoFinal: pontuacaoFinal.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    percentualSalarioSemCreditoSemCampanha: mediaSemCredito,
    percentualSalarioFinal: shouldZero ? 0 : (isMesAtualPath && pontuacaoCampanha === 0 ? mediaSemCredito : mediaFinal),
  };
}, [filtrarReflexPorPleito, getMesAtual, isHistoricoPath, isMesAtualPath, vendedorSelecionado, selectedVendedorMes, selectedVendedorId, usuario?.id]);



const { semCredito, comCredito } = useMemo(() => {
  if (!vendedorSelecionado) return { semCredito: [], comCredito: [] };

  let vendedorSelecionadoReal = vendedorSelecionado;

  if (Array.isArray(vendedorSelecionado) && vendedorSelecionado.length > 1) {
    if (selectedVendedorId === 0) {
      const encontrado = vendedorSelecionado.find(
        v => Number(v.id) === Number(usuario?.id)
      );
      if (encontrado) vendedorSelecionadoReal = encontrado;
    } else {
      const encontrado = vendedorSelecionado.find(
        v => Number(v.id) === Number(selectedVendedorId)
      );
      if (encontrado) vendedorSelecionadoReal = encontrado;
    }
  }

  const meses = vendedorSelecionadoReal?.meses;
  if (!meses?.length) return { semCredito: [], comCredito: [] };

  const mesAtual = getMesAtual(meses);
  const reflexFiltrado = filtrarReflexPorPleito(mesAtual?.reflex || []);

  const mesAtualDate = parseMes(mesAtual?.mes);
  const now = new Date();
  const isMesAtualCorrente = mesAtualDate
    ? mesAtualDate.getFullYear() === now.getFullYear() && mesAtualDate.getMonth() === now.getMonth()
    : false;

  const semCredito = [];
  const comCredito = [];
  const useProjectedPoints = !isHistoricoPath;

  for (const { kpi, pesoPercentual, atingimentos, tipo } of reflexFiltrado || []) {
    if (!atingimentos) continue;

    const { projetado, real, realComCredito, final } = atingimentos;
    const isCampanha = isTipoCampanha(tipo);
    const itemNome = isCampanha ? "CAMPANHA" : kpi;

    if (projetado != null) {
      const pontosSemCredito = getPontosSemCredito({
        isCampanha,
        atingimentos,
        useProjected: useProjectedPoints,
      });

      semCredito.push({
        nome: itemNome,
        percentual: pesoPercentual,
        pontos: pontosSemCredito,
        ponderados: pontosSemCredito,
        isCampanha,
      });
    }

    if (realComCredito != null) {
      comCredito.push({
        nome: itemNome,
        percentual: pesoPercentual,
        pontos: isCampanha ? real : final,
        ponderados: final,
        isCampanha,
      });
    }
  }

  if (isMesAtualCorrente) {
    comCredito.length = 0;
  }

  const itensValidosSemCredito = semCredito.filter((i) => !i.isCampanha);
  const itensValidosComCredito = comCredito.filter((i) => !i.isCampanha);

  const mediaSemCredito = itensValidosSemCredito.reduce((sum, item) => sum + item.ponderados, 0);
  const mediaComCredito = itensValidosComCredito.reduce((sum, item) => sum + item.ponderados, 0);

  const campanhaSemCredito = semCredito.find((i) => i.isCampanha);
  const campanhaComCredito = comCredito.find((i) => i.isCampanha);

  const gatilhoNaoAtingido = temGatilhoNaoAtingido(reflexFiltrado);
  const shouldZeroByGatilho = isHistoricoPath && gatilhoNaoAtingido;
  const shouldZeroSemCreditoByMin = mediaSemCredito < 100;
  const shouldZeroComCreditoByMin = mediaComCredito < 100;
  const pontuacaoFinalSemCredito = (shouldZeroByGatilho || shouldZeroSemCreditoByMin) ? 0 : mediaSemCredito + (campanhaSemCredito?.pontos ?? 0);
  const pontuacaoFinalComCredito = (shouldZeroByGatilho || shouldZeroComCreditoByMin) ? 0 : mediaComCredito + (campanhaComCredito?.pontos ?? 0);

  const todosItensValidos = reflexFiltrado?.filter((item) => isTipoReflex(item.tipo));


  const percentualSemCredito = Math.round(
    todosItensValidos.reduce((acc, item) => {
      if (isMesAtualCorrente) {

        return acc + Number(item.percentualSalarioFinal || 0);
      } else {

        return acc + Number(item.percentualSalarioSemCredito || 0);
      }
    }, 0) / (todosItensValidos.length || 1)
  );


  const percentualComCredito = Math.round(
    todosItensValidos.reduce((acc, item) => {
        return acc + Number(item.percentualSalarioFinal || 0);

    }, 0) / (todosItensValidos.length || 1)
  );


  const formatar = (valor) =>
    valor == null
      ? ""
      : valor.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  const formatarLista = (lista, media, pontuacaoFinal, percentual) => {
    const campanhas = lista.filter((item) => item.isCampanha);
    const normais = lista.filter((item) => !item.isCampanha);

    return [
      ...normais.map((item) => ({
        ...item,
        pontos: formatar(item.pontos),
      })),
      {
        nome: "Soma de Pontos",
        percentual: null,
        pontos: formatar(media),
      },
      ...campanhas.map((item) => ({
        ...item,
        pontos: formatar(item.pontos),
      })),
      {
        nome: "Pontuação Final",
        percentual: null,
        pontos: pontuacaoFinal > 500 ? formatar(500) : formatar(pontuacaoFinal),
        sub: `${percentual.toLocaleString("pt-BR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}% do salário`,
      },
    ];
  };

  const percentualFinalSemCredito = shouldZeroByGatilho ? 0 : percentualSemCredito;
  const percentualFinalComCredito = shouldZeroByGatilho ? 0 : percentualComCredito;

  const listaSemCredito = semCredito.length
    ? formatarLista(
        semCredito,
        mediaSemCredito,
        pontuacaoFinalSemCredito,
        percentualFinalSemCredito
      )
    : [];

  const listaComCredito = comCredito.length
    ? formatarLista(
        comCredito,
        mediaComCredito,
        pontuacaoFinalComCredito,
        percentualFinalComCredito
      )
    : [];

  return { semCredito: listaSemCredito, comCredito: listaComCredito };
}, [filtrarReflexPorPleito, getMesAtual, isHistoricoPath, vendedorSelecionado, selectedVendedorId, usuario?.id]);


const isResumoCoordenacao = useMemo(() => {
  if (!vendedorSelecionado || Array.isArray(vendedorSelecionado)) return false;

  return getMesResumoCoordenacao(vendedorSelecionado).isResumoCoordenacao;
}, [getMesResumoCoordenacao, vendedorSelecionado]);




const dataCardsRaw = useMemo(() => {
  if (!vendedorSelecionado?.meses?.length) return [];
  const { mesAtual } = getMesResumoCoordenacao(vendedorSelecionado);
  // Log da data usada para depuração
  try {
    console.log('dataCards - mesAtual usado:', mesAtual?.mes, '->', parseMes(mesAtual?.mes));
  } catch (e) {
    console.log('dataCards - mesAtual (sem parse):', mesAtual?.mes);
  }
  return mesAtual?.reflex || [];
}, [getMesResumoCoordenacao, vendedorSelecionado]);

const hasPleitoNoMes = useMemo(() => (
  isHistoricoPath && dataCardsRaw?.some((item) => item?.pleito === true)
), [dataCardsRaw, isHistoricoPath]);

useEffect(() => {
  if (!hasPleitoNoMes && mostrarPleito) {
    setMostrarPleito(false);
  }
}, [hasPleitoNoMes, mostrarPleito]);

const dataCards = useMemo(() => (
  filtrarReflexPorPleito(dataCardsRaw) || []
), [dataCardsRaw, filtrarReflexPorPleito]);

const mesMaisRecenteGlobalVision = useMemo(() => {
  if (!Array.isArray(vendedorSelecionado) || vendedorSelecionado.length === 0) {
    return null;
  }

  const todosMeses = vendedorSelecionado.flatMap((vendedor) => vendedor?.meses || []);
  return getMesMaisRecente(todosMeses);
}, [vendedorSelecionado]);

const vision = useMemo(() => {
  if (!Array.isArray(vendedorSelecionado) || vendedorSelecionado.length === 0) {
    return [];
  }

  const mesGlobal = mesMaisRecenteGlobalVision?.mes ?? null;
  const vendedoresComMesGlobal = mesGlobal
    ? vendedorSelecionado.filter((vendedor) =>
        (vendedor?.meses || []).some((mes) => mes.mes === mesGlobal)
      )
    : [];
  const deveUsarFallbackIndividual = !selectedVendedorMes && vendedoresComMesGlobal.length === 0;

  return vendedorSelecionado
    .map((vendedor) => {
      const meses = vendedor?.meses || [];

      if (!meses.length) return null;
      let mesAtual = null;

      if (selectedVendedorMes) {
        mesAtual = meses.find((m) => m.mes === selectedVendedorMes.value);

        if (!mesAtual) {
          return null;
        }
      } else if (deveUsarFallbackIndividual) {
        mesAtual = getMesMaisRecente(meses);
      } else {
        mesAtual = meses.find((m) => m.mes === mesGlobal);

        if (!mesAtual) {
          return null;
        }
      }

      if (!mesAtual?.reflex?.length) return null;

      const itensSemDuplicados = removerDuplicadosPriorizandoPleito(mesAtual.reflex);

      const campanha = itensSemDuplicados
        .filter(i => isTipoCampanha(i.tipo))
        .reduce((acc, i) => acc + getCampanhaPontuacao(i), 0);

      const { real, proj } = itensSemDuplicados.reduce(
        (acc, item) => {
          if (isTipoCampanha(item.tipo)) return acc;

          acc.real += Number(item.atingimentos?.semProjecao || 0);
          acc.proj += Number(item.atingimentos?.ponderadoProjetadoFinal || 0);
          return acc;
        },
        { real: 0, proj: 0 }
      );

      return {
        id: vendedor.id,
        nome: vendedor.nomeVendedor,
        real,
        proj,
        campanha
      };
    })
    .filter(Boolean);
  }, [mesMaisRecenteGlobalVision, selectedVendedorMes, vendedorSelecionado]);

const handleSelectVisionVendedor = useCallback((vendedor) => {
  if (!vendedor?.id) return;

  setSelectedVendedorId(Number(vendedor.id));

  if (typeof globalThis.window?.scrollTo === "function") {
    globalThis.window.scrollTo({ top: 0, behavior: "smooth" });
  }
}, []);


const mesesOptions = useMemo(() => {
  if (!vendedorSelecionado?.meses?.length) return [];

  return vendedorSelecionado.meses.map(({ mes }) => {
    const [abreviacao, anoAbreviado] = mes.split("/");
    const mesesMap = {
      jan: "Janeiro",
      fev: "Fevereiro",
      mar: "Março",
      abr: "Abril",
      mai: "Maio",
      jun: "Junho",
      jul: "Julho",
      ago: "Agosto",
      set: "Setembro",
      out: "Outubro",
      nov: "Novembro",
      dez: "Dezembro",
    };
    const nomeMes = mesesMap[abreviacao.toLowerCase()] || mes;
    const anoCompleto = `20${anoAbreviado}`;

    return {
      label: `${nomeMes} de ${anoCompleto}`,
      value: mes,
    };
  });
}, [vendedorSelecionado]);

const mesRecente = obterMesesMaisRecentes(vendedorSelecionado, vendedoresParaSelect);

const isMesAtualSelecionado = useMemo(() => {
  if (!vendedorSelecionado?.meses?.length) return false;
  const mesAtualObj = getMesAtual(vendedorSelecionado.meses);
  if (!mesAtualObj?.mes) return false;
  const parsed = parseMes(mesAtualObj.mes);
  if (!parsed) return false;
  const now = new Date();
  return parsed.getFullYear() === now.getFullYear() && parsed.getMonth() === now.getMonth();
}, [getMesAtual, vendedorSelecionado]);

  const [modalShown, setModalShown] = useState(false);

    useEffect(() => {
    setModalShown(false);
  }, [selectedVendedorId]);

const gatinhoVendedores = useMemo(() => {
  if (!vendedorSelecionado) return [];

  const isArray = Array.isArray(vendedorSelecionado);

  const filtrarReflexValidos = reflex =>
    reflex?.filter(item => !isTipoCampanha(item?.tipo)) || [];

  const passaNoGatilho = item => isIndicadorGatilhoNaoAtingido(item);

  if (!isArray) {
    const mesAtual = getMesAtual(vendedorSelecionado?.meses);

    const reflex = filtrarReflexValidos(mesAtual?.reflex);

    return reflex.filter(passaNoGatilho).map(item => item.kpi);
  }

  const nomes = [];

  for (const vendedor of vendedorSelecionado) {
    const mesAtual = getMesAtual(vendedor?.meses);
    const reflex = filtrarReflexValidos(mesAtual?.reflex);

    const algumComGatilho = reflex.some(passaNoGatilho);

    if (algumComGatilho) {
      nomes.push(vendedor.nomeVendedor);
    }
  }

  return nomes;
}, [vendedorSelecionado, getMesAtual]);




  useEffect(() => {
    if (!modalShown && gatinhoVendedores.length > 0 && globalThis.location.pathname !== "/reflex/historico") {
      openModalAction();
      setModalShown(true);
    }
  }, [gatinhoVendedores, modalShown, openModalAction]);


  const mensagemGatilho = useMemo(() => {
  if (selectedVendedorId !== 0 ) {
    return "Você ainda não atingiu o Indicador Gatilho";
  }

  if (selectedVendedorId === 0 && !Array.isArray(vendedorSelecionado)) {
   return "Você ainda não atingiu o Indicador Gatilho";
 }
  if (selectedVendedorId === 0) {
    return "Você tem vendedores no time que ainda não atingiram o Gatilho:";
  }

  return "";
}, [selectedVendedorId, vendedorSelecionado]);



  return (
    <div className="">

      <div className="bg-white pt-4">
        {globalThis.location.pathname === `/reflex/historico` && (
          <>
            <p className="font-bold pt-4 text-[#000000] text-2xl text-center mx-auto max-w-[900px]">
              *ATENÇÃO: Resultados podem sofrer alterações no fechamento até o 3º dia útil do mês ou conforme política de
              pleitos
            </p>
            {hasPleitoNoMes ? (
              <div className="relative lg:-top-6 w-full mx-auto mt-5 lg:mt-0 flex justify-center lg:justify-end lg:pr-4">
                <div className="lg:absolute inline-flex rounded-full border-2 border-[#01054C] bg-white p-1">
                  <button
                    type="button"
                    onClick={() => setMostrarPleito(false)}
                    aria-pressed={!mostrarPleito}
                    className={`px-4 py-1 rounded-full text-sm font-semibold transition-colors ${!mostrarPleito ? "bg-[#01054C] text-white" : "text-[#01054C]"}`}
                  >
                    Sem pleito
                  </button>
                  <button
                    type="button"
                    onClick={() => setMostrarPleito(true)}
                    aria-pressed={mostrarPleito}
                    className={`px-4 py-1 rounded-full text-sm font-semibold transition-colors ${mostrarPleito ? "bg-[#F5C115] text-[#000000]" : "text-[#01054C]"}`}
                  >
                    Com pleito
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative lg:-top-6 w-full mx-auto mt-5 lg:mt-0 flex justify-center lg:justify-end lg:pr-4">
                <Link
                  to={LINK_SOLICITAR_PLEITO}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lg:absolute inline-block rounded-full bg-[#01054C] text-white px-4 py-2 text-sm font-semibold hover:brightness-110 transition-colors"
                >
                  Solicitar Pleito
                </Link>
              </div>
            )}
          </>
        )}
        <div className="bg-white border border-gray-200 rounded-lg shadow-md p-8 mt-8">
          <ReflexSelect selectOptions={selectOptions} mesRecente={mesRecente} setSelectedVendedorId={setSelectedVendedorId} mesesOptions={mesesOptions} selectedVendedorId={selectedVendedorId} mesAtual={true} vendedores={vendedoresParaSelect}  setSelectedVendedorMes={setSelectedVendedorMes} vendedorSelecionado={vendedorSelecionado} loading={loading}/>
          <CardsPoints points={points} selectedVendedorMes={selectedVendedorMes} />
        </div>
      </div>

      {vendedorSeminfo ? (
        <p className=" text-center mt-4 font-bold text-1xl max-w-[500px] mx-auto mt-8">
          {vendedorSeminfo}
        </p>
      ) : (<>
          {!isResumoCoordenacao && (
            <>
              <SeeMore semCredito={semCredito} comCredito={comCredito} selectedVendedorMes={selectedVendedorMes} open={open} setOpen={setOpen} />

              <Cards dataCards={dataCards} vendedores={vendedoresParaSelect} isHistorico={isHistoricoPath} isMesAtual={isMesAtualSelecionado} />
            </>
          )}
          {selectedVendedorId === 0 && vendedoresParaSelect.length > 1 && (
            <Vision vision={vision} onSelectVendedor={handleSelectVisionVendedor} />
          )}



          <ModalReflex
            isOpen={isModalOpenAction}
            status="alert"
            message={mensagemGatilho}
            list={gatinhoVendedores}
            submessage="Acelere e não deixe de acumular pontos no mês!"
            closeModal={closeModalAction}
          />
          </>)}
    </div>
  );
};

export default ReflexCurrentMonth;
